import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronDown,
  FileText,
  Loader2,
  MessageCircle,
  PencilLine,
  Upload,
  X,
} from 'lucide-react';

import Button from '../../components/ui/Button/Button';
import ChatInterface, {
  type ChatMessage,
} from '../../components/ui/ChatInterface/ChatInterface';
import FileDrop from '../../components/ui/FileDrop/FileDrop';
import RadarChart, {
  type RadarDataPoint,
} from '../../components/ui/Charts/RadarChart/RadarChart';
import TextArea from '../../components/ui/TextArea/TextArea';
import {
  feedbackService,
  type DimensionScore,
  type FeedbackResponse,
  type Submission,
  type SubmissionChatActiveGeneration,
  type SubmissionChatMessage,
  type SubmissionChatStreamContentEvent,
  type SubmissionChatStreamDoneEvent,
  type SubmissionChatStreamErrorEvent,
  type SubmissionChatStreamSessionEvent,
  type SubmissionChatSnapshotEvent,
} from '../../services/feedbackService';
import {
  projectService,
  type Project,
  type SubmissionSummary,
} from '../../services/projectService';
import { waitForSubmissionProcessing } from '../../services/submissionProcessingService';
import { getSubmissionFileDisplayNames } from '../../lib/submissionFiles';

import './ProjectReport.css';

interface ReportState {
  project: Project;
  submissions: SubmissionSummary[];
  feedback: FeedbackResponse | null;
}

interface ScoreSummary {
  current: number;
  potential: number;
  delta: number;
}

interface RubricGroup {
  id: string;
  label: string;
  shortLabel: string;
  color: string;
  dimensions: DimensionScore[];
}

interface RubricGroupDefinition {
  id: string;
  label: string;
  shortLabel: string;
  color: string;
}

const RUBRIC_GROUPS: RubricGroupDefinition[] = [
  {
    id: 'purposefulness',
    label: 'Purposefulness',
    shortLabel: 'Purpose',
    color: '#003D7C',
  },
  {
    id: 'disciplinary-grounding',
    label: 'Disciplinary Grounding',
    shortLabel: 'Grounding',
    color: '#0D7377',
  },
  {
    id: 'integration',
    label: 'Integration',
    shortLabel: 'Integration',
    color: '#B8860B',
  },
  {
    id: 'critical-awareness',
    label: 'Critical Awareness',
    shortLabel: 'Critical',
    color: '#C8102E',
  },
  {
    id: 'other',
    label: 'Other Rubrics',
    shortLabel: 'Other',
    color: '#546A7B',
  },
];

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const fallbackDimensionColors = [
  '#003D7C',
  '#3670A8',
  '#0D7377',
  '#1A9DA2',
  '#47BBBE',
  '#B8860B',
  '#DAA520',
  '#C8102E',
  '#E8546A',
];

const hexToRgba = (hexColor: string, alpha: number) => {
  const normalizedHex = hexColor.replace('#', '');

  if (normalizedHex.length !== 6) {
    return `rgba(84, 106, 123, ${alpha})`;
  }

  const red = Number.parseInt(normalizedHex.slice(0, 2), 16);
  const green = Number.parseInt(normalizedHex.slice(2, 4), 16);
  const blue = Number.parseInt(normalizedHex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const formatDate = (value?: string) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return dateFormatter.format(parsed);
};

const getDimensionColor = (dimension: DimensionScore, index: number) =>
  dimension.dimension_color || fallbackDimensionColors[index % fallbackDimensionColors.length];

const getRubricGroup = (dimension: DimensionScore): RubricGroupDefinition => {
  if (dimension.dimension_id === 1 || dimension.dimension_id === 2) {
    return RUBRIC_GROUPS[0];
  }

  if (
    dimension.dimension_id === 3 ||
    dimension.dimension_id === 4 ||
    dimension.dimension_id === 5
  ) {
    return RUBRIC_GROUPS[1];
  }

  if (dimension.dimension_id === 6 || dimension.dimension_id === 7) {
    return RUBRIC_GROUPS[2];
  }

  if (dimension.dimension_id === 8 || dimension.dimension_id === 9) {
    return RUBRIC_GROUPS[3];
  }

  const normalizedLabel = dimension.dimension_label.toLowerCase();
  if (normalizedLabel.includes('frame') || normalizedLabel.includes('stakeholder')) {
    return RUBRIC_GROUPS[0];
  }

  if (
    normalizedLabel.includes('disciplinary') ||
    normalizedLabel.includes('reasoning') ||
    normalizedLabel.includes('credibility')
  ) {
    return RUBRIC_GROUPS[1];
  }

  if (normalizedLabel.includes('integration')) {
    return RUBRIC_GROUPS[2];
  }

  if (
    normalizedLabel.includes('impact') ||
    normalizedLabel.includes('limitation') ||
    normalizedLabel.includes('critical')
  ) {
    return RUBRIC_GROUPS[3];
  }

  return RUBRIC_GROUPS[4];
};

const getShortDimensionLabel = (dimension: DimensionScore) => {
  const labelMap: Record<number, string> = {
    1: 'Framing',
    2: 'Stakeholder',
    3: 'Range',
    4: 'Reasoning',
    5: 'Credibility',
    6: 'Integration Num',
    7: 'Integration Depth',
    8: 'Social Impact',
    9: 'Limitations',
  };

  return labelMap[dimension.dimension_id] || dimension.dimension_label;
};

const getScoreValue = (dimension: DimensionScore) => dimension.ai_score ?? dimension.personal_score ?? 0;

const getSubmissionContent = (submission?: Submission) => {
  if (!submission) {
    return '';
  }

  if (submission.essay_text?.trim()) {
    return submission.essay_text;
  }

  if (submission.file_urls?.length) {
    return getSubmissionFileDisplayNames(
      submission.file_urls,
      submission.file_names,
    ).join('\n');
  }

  return '';
};

const isFileSubmission = (submission?: Submission) =>
  Boolean(submission?.file_urls?.length && !submission.essay_text?.trim());

const getLatestSubmissionId = (
  project: Project,
  submissions: SubmissionSummary[],
) => {
  if (project.latest_submission_id) {
    return project.latest_submission_id;
  }

  return submissions[submissions.length - 1]?.id;
};

const createChatClientId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getChatMetadataString = (
  metadata: Record<string, unknown>,
  key: string,
) => {
  const value = metadata[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
};

const ProjectReport = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeWaitSessionRef = useRef(0);
  const isMountedRef = useRef(false);
  const chatClientIdRef = useRef(createChatClientId());
  const resubmitPanelRef = useRef<HTMLElement | null>(null);

  const [reportState, setReportState] = useState<ReportState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDimensionId, setSelectedDimensionId] = useState<number | null>(null);
  const [isResubmitOpen, setIsResubmitOpen] = useState(false);
  const [resubmitText, setResubmitText] = useState('');
  const [resubmitFiles, setResubmitFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(null);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatError, setChatError] = useState<string | null>(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isChatGenerating, setIsChatGenerating] = useState(false);
  const [isChatClearing, setIsChatClearing] = useState(false);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(false);

  const loadReport = useCallback(async (submissionIdOverride?: string) => {
    if (!projectId) {
      setError('Missing project id.');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const project = await projectService.getProject(projectId);
      const submissionsResponse = await projectService.getProjectSubmissions(projectId);
      const requestedSubmissionId = submissionIdOverride ?? searchParams.get('submissionId');
      const latestSubmissionId =
        requestedSubmissionId || getLatestSubmissionId(project, submissionsResponse.submissions);
      const feedback = latestSubmissionId
        ? await feedbackService.getFeedback(latestSubmissionId)
        : null;

      setReportState({
        project,
        submissions: submissionsResponse.submissions,
        feedback,
      });
      setSelectedDimensionId(feedback?.dimensions[0]?.dimension_id ?? null);
    } catch (loadError) {
      console.error('Failed to load project report:', loadError);
      setError('Failed to load this report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, searchParams]);

  const mapSubmissionChatMessage = useCallback(
    (message: SubmissionChatMessage): ChatMessage => {
      const authorName = getChatMetadataString(
        message.metadata,
        'author_display_name',
      );
      const authorRole = getChatMetadataString(
        message.metadata,
        'author_project_role',
      );

      return {
        id: message.id,
        sender: message.role === 'user' ? 'user' : 'llm',
        message: message.content,
        timestamp: new Date(message.created_at),
        ...(authorName ? { authorName } : {}),
        ...(authorRole ? { authorRole } : {}),
      };
    },
    [],
  );

  const buildActiveGenerationMessage = useCallback(
    (activeGeneration: SubmissionChatActiveGeneration): ChatMessage => ({
      id: `stream-${activeGeneration.generation_id}`,
      sender: 'llm',
      message: activeGeneration.assistant_content,
      timestamp: new Date(activeGeneration.started_at),
      isStreaming: true,
      generationId: activeGeneration.generation_id,
    }),
    [],
  );

  const applyChatSnapshot = useCallback(
    (event: SubmissionChatSnapshotEvent) => {
      const nextMessages = event.messages.map(mapSubmissionChatMessage);
      if (event.active_generation) {
        nextMessages.push(buildActiveGenerationMessage(event.active_generation));
      }

      setChatMessages(nextMessages);
      setIsChatGenerating(Boolean(event.active_generation));
      setIsChatHistoryLoading(false);
    },
    [buildActiveGenerationMessage, mapSubmissionChatMessage],
  );

  const applyChatSessionStart = useCallback(
    (event: SubmissionChatStreamSessionEvent) => {
      const userMessage = mapSubmissionChatMessage(event.user_message);
      const assistantMessageId = `stream-${event.generation_id}`;

      setChatMessages((previousMessages) => {
        const messagesWithUser = previousMessages.some(
          (message) => message.id === userMessage.id,
        )
          ? previousMessages.map((message) =>
              message.id === userMessage.id ? userMessage : message,
            )
          : [...previousMessages, userMessage];

        if (messagesWithUser.some((message) => message.id === assistantMessageId)) {
          return messagesWithUser;
        }

        return [
          ...messagesWithUser,
          {
            id: assistantMessageId,
            sender: 'llm',
            message: '',
            timestamp: new Date(),
            isStreaming: true,
            generationId: event.generation_id,
          },
        ];
      });
      setIsChatGenerating(true);
    },
    [mapSubmissionChatMessage],
  );

  const applyChatContent = useCallback(
    (event: SubmissionChatStreamContentEvent) => {
      const assistantMessageId = `stream-${event.generation_id}`;
      setChatMessages((previousMessages) => {
        let foundMessage = false;
        const nextMessages = previousMessages.map((message) => {
          if (message.id !== assistantMessageId) {
            return message;
          }

          foundMessage = true;
          return {
            ...message,
            message: `${message.message}${event.content}`,
            isStreaming: true,
          };
        });

        if (foundMessage) {
          return nextMessages;
        }

        return [
          ...nextMessages,
          {
            id: assistantMessageId,
            sender: 'llm',
            message: event.content,
            timestamp: new Date(),
            isStreaming: true,
            generationId: event.generation_id,
          },
        ];
      });
    },
    [],
  );

  const applyChatFinal = useCallback(
    (event: SubmissionChatStreamDoneEvent | SubmissionChatStreamErrorEvent) => {
      setChatMessages(event.messages.map(mapSubmissionChatMessage));
      setIsChatGenerating(false);
      setIsChatHistoryLoading(false);
    },
    [mapSubmissionChatMessage],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  useEffect(() => {
    if (searchParams.get('resubmit') === '1') {
      setIsResubmitOpen(true);
    }
  }, [searchParams]);

  const feedback = reportState?.feedback ?? null;
  const submission = feedback?.submission;
  const dimensions = useMemo(() => feedback?.dimensions ?? [], [feedback]);
  const currentVersion = submission?.iteration_number ?? reportState?.submissions.length ?? 1;
  const isFileBasedSubmission = isFileSubmission(submission);
  const submissionContent = getSubmissionContent(submission);
  const chatSubmissionId = submission?.id ?? null;
  const instructorSuggestion = submission?.instructor_suggestion ?? null;
  const isChatAvailable = Boolean(
    chatSubmissionId && submission?.ai_processing_status === 'completed',
  );

  useEffect(() => {
    setChatMessages([]);
    setChatError(null);
    setIsChatGenerating(false);
    setIsChatHistoryLoading(false);
  }, [chatSubmissionId]);

  useEffect(() => {
    if (!chatSubmissionId || !isChatAvailable) {
      return;
    }

    const abortController = new AbortController();
    setIsChatHistoryLoading(true);

    feedbackService
      .subscribeSubmissionChat(
        chatSubmissionId,
        chatClientIdRef.current,
        {
          onSnapshot: applyChatSnapshot,
          onSession: applyChatSessionStart,
          onContent: applyChatContent,
          onDone: applyChatFinal,
          onError: (event) => {
            applyChatFinal(event);
            setChatError(event.message);
          },
        },
        abortController.signal,
      )
      .catch((subscribeError) => {
        if (subscribeError instanceof Error && subscribeError.name === 'AbortError') {
          return;
        }

        console.error('Failed to subscribe to report assistant:', subscribeError);
        setIsChatHistoryLoading(false);
        setChatError('Live assistant updates are disconnected. Refresh to reconnect.');
      });

    return () => {
      abortController.abort();
    };
  }, [
    applyChatContent,
    applyChatFinal,
    applyChatSessionStart,
    applyChatSnapshot,
    chatSubmissionId,
    isChatAvailable,
  ]);

  const scoreSummary: ScoreSummary = useMemo(() => {
    const current = dimensions.reduce(
      (total, dimension) => total + getScoreValue(dimension),
      0,
    );
    const potential = dimensions.length * 3;

    return {
      current,
      potential,
      delta: Math.max(0, potential - current),
    };
  }, [dimensions]);

  const radarData: RadarDataPoint[] = useMemo(
    () =>
      dimensions.map((dimension) => ({
        dimension: getShortDimensionLabel(dimension),
        userScore: getScoreValue(dimension),
        classAverage: 2.5,
      })),
    [dimensions],
  );

  const selectedDimension = useMemo(
    () => dimensions.find((dimension) => dimension.dimension_id === selectedDimensionId) ?? null,
    [dimensions, selectedDimensionId],
  );

  const groupedDimensions: RubricGroup[] = useMemo(
    () =>
      RUBRIC_GROUPS.map((group) => ({
        ...group,
        dimensions: dimensions.filter(
          (dimension) => getRubricGroup(dimension).id === group.id,
        ),
      })).filter((group) => group.dimensions.length > 0),
    [dimensions],
  );

  const canSubmit = isFileBasedSubmission
    ? resubmitFiles.length > 0
    : resubmitText.trim().length > 0;

  const handleStartResubmit = () => {
    setIsResubmitOpen(true);
    setSubmissionMessage(null);
    setResubmitText('');
    setResubmitFiles([]);
  };

  useEffect(() => {
    if (!isResubmitOpen) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const panel = resubmitPanelRef.current;
      if (!panel) {
        return;
      }

      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const textarea = panel.querySelector<HTMLTextAreaElement>('textarea');
      const focusTarget = textarea ?? panel;
      window.setTimeout(() => {
        focusTarget.focus({ preventScroll: true });
      }, 250);
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isResubmitOpen]);

  const handleCancelResubmit = () => {
    setIsResubmitOpen(false);
    setSubmissionMessage(null);
    setResubmitText('');
    setResubmitFiles([]);
  };

  const handleResubmit = async () => {
    if (!projectId || !reportState || !canSubmit) {
      return;
    }

    const nextVersion = reportState.submissions.length + 1;
    const confirmed = window.confirm(
      `Submit version ${nextVersion}? This will trigger a new AI evaluation and keep previous submissions available.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSubmissionMessage('Submitting your new version...');

      const result = await projectService.resubmitProject(
        projectId,
        isFileBasedSubmission ? undefined : resubmitText,
        isFileBasedSubmission ? resubmitFiles : undefined,
      );

      setIsResubmitOpen(false);
      setResubmitFiles([]);
      setResubmitText('');
      setSubmissionMessage('AI is processing your new version...');
      setSearchParams({ submissionId: result.submission_id }, { replace: true });

      const waitSession = activeWaitSessionRef.current + 1;
      activeWaitSessionRef.current = waitSession;
      const waitResult = await waitForSubmissionProcessing(result.submission_id, {
        timeoutMs: 60000,
      });

      if (!isMountedRef.current || activeWaitSessionRef.current !== waitSession) {
        return;
      }

      if (waitResult.status === 'failed') {
        setSubmissionMessage('The new version was submitted, but AI evaluation failed.');
      } else {
        setSubmissionMessage('New version submitted.');
      }

      await loadReport(result.submission_id);
    } catch (submitError) {
      console.error('Failed to resubmit from report:', submitError);
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Failed to submit a new version.',
      );
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleSendChatMessage = useCallback(
    async (message: string) => {
      if (!chatSubmissionId || !isChatAvailable) {
        setChatError('AI feedback is not ready for chat yet.');
        return;
      }

      if (isChatGenerating) {
        setChatError('Wait for the current response before sending another message.');
        return;
      }

      setIsChatLoading(true);
      setChatError(null);

      let receivedSession = false;

      try {
        await feedbackService.streamSubmissionChatMessage(
          chatSubmissionId,
          message,
          {
            onSession: (event) => {
              receivedSession = true;
              applyChatSessionStart(event);
            },
            onContent: applyChatContent,
            onDone: applyChatFinal,
            onError: (event) => {
              applyChatFinal(event);
              setChatError(event.message);
            },
          },
          chatClientIdRef.current,
        );
      } catch (sendError) {
        console.error('Failed to send report assistant message:', sendError);
        if (!receivedSession) {
          setIsChatGenerating(false);
        }
        setChatError(
          sendError instanceof Error
            ? sendError.message
            : 'Failed to send message. Please try again.',
        );
      } finally {
        setIsChatLoading(false);
      }
    },
    [
      applyChatContent,
      applyChatFinal,
      applyChatSessionStart,
      chatSubmissionId,
      isChatAvailable,
      isChatGenerating,
    ],
  );

  const handleClearChat = useCallback(async () => {
    if (!chatSubmissionId || isChatClearing) {
      return;
    }

    if (isChatGenerating) {
      setChatError('Wait for the current response before clearing chat.');
      return;
    }

    try {
      setIsChatClearing(true);
      setChatError(null);
      const response = await feedbackService.clearSubmissionChat(
        chatSubmissionId,
        chatClientIdRef.current,
      );
      setChatMessages(response.messages.map(mapSubmissionChatMessage));
      setIsChatGenerating(Boolean(response.session.active_generation_id));
    } catch (clearError) {
      console.error('Failed to clear report assistant chat:', clearError);
      setChatError(
        clearError instanceof Error
          ? clearError.message
          : 'Failed to clear chat. Please try again.',
      );
    } finally {
      setIsChatClearing(false);
    }
  }, [
    chatSubmissionId,
    isChatClearing,
    isChatGenerating,
    mapSubmissionChatMessage,
  ]);

  const isChatBusy = isChatLoading || isChatGenerating || isChatHistoryLoading || isChatClearing;
  const chatPlaceholder = !isChatAvailable
    ? 'AI chat is available after feedback is complete'
    : isChatGenerating
      ? 'A response is being generated...'
      : 'Ask about your feedback...';
  const chatLoadingLabel = isChatHistoryLoading
    ? 'Loading...'
    : isChatClearing
      ? 'Clearing...'
      : 'Generating...';

  if (isLoading) {
    return (
      <section className="project-report project-report--status">
        <div className="project-report__status-panel">
          <Loader2 className="spin" size={22} strokeWidth={2.4} />
          <p>Loading report...</p>
        </div>
      </section>
    );
  }

  if (error && !reportState) {
    return (
      <section className="project-report project-report--status">
        <div className="project-report__status-panel is-error">
          <AlertCircle size={22} strokeWidth={2.4} />
          <p>{error}</p>
          <Button variant="blue" onClick={() => void loadReport()}>
            Retry
          </Button>
        </div>
      </section>
    );
  }

  if (!reportState) {
    return null;
  }

  const courseLabel =
    reportState.project.course_code ||
    reportState.project.course_title ||
    'Unassigned course';

  return (
    <section className="project-report">
      <header className="project-report__topbar">
        <button
          type="button"
          className="project-report__back-button"
          onClick={() => navigate('/project')}
        >
          <ArrowLeft size={16} strokeWidth={2.4} />
          <span>Projects</span>
        </button>

        <div className="project-report__heading">
          <h1>{reportState.project.title}</h1>
          <p>
            {courseLabel} · Submitted{' '}
            {formatDate(submission?.submitted_at || reportState.project.submission_date)}
          </p>
        </div>
      </header>

      {error && (
        <div className="project-report__notice is-error">
          <AlertCircle size={16} strokeWidth={2.4} />
          <span>{error}</span>
        </div>
      )}

      {submissionMessage && (
        <div className="project-report__notice">
          {isSubmitting ? (
            <Loader2 className="spin" size={16} strokeWidth={2.4} />
          ) : (
            <Check size={16} strokeWidth={2.4} />
          )}
          <span>{submissionMessage}</span>
        </div>
      )}

      <div className="project-report__notice">
        <FileText size={16} strokeWidth={2.4} />
        <span>
          This report is version {currentVersion}. Revise from the feedback below and
          submit a new version from this page.
        </span>
      </div>

      <div className="project-report__grid">
        <aside className="project-report__side-panel">
          <section className="project-report__card">
            <h2>Submission</h2>
            <dl className="project-report__meta-list">
              <div>
                <dt>Version</dt>
                <dd>{currentVersion}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{submission?.ai_processing_status || reportState.project.status}</dd>
              </div>
              <div>
                <dt>Total Versions</dt>
                <dd>{reportState.submissions.length || 1}</dd>
              </div>
            </dl>
          </section>

          <section className="project-report__card project-report__card--scores">
            <h2>Your Scores</h2>
            {radarData.length > 0 ? (
              <div className="project-report__radar">
                <RadarChart
                  data={radarData}
                  selectedDimensions={radarData.map((point) => point.dimension)}
                  maxScore={3}
                  height={252}
                />
              </div>
            ) : (
              <p className="project-report__muted">Scores are not available yet.</p>
            )}

            <div className="project-report__score-summary">
              <div className="project-report__score-block">
                <span>Current</span>
                <strong>
                  {scoreSummary.current.toFixed(1)}
                  <small>/ {scoreSummary.potential}</small>
                </strong>
              </div>
              <div className="project-report__score-block project-report__score-block--potential">
                <span>Maximum</span>
                <strong>
                  {scoreSummary.potential}
                  <small>/ {scoreSummary.potential}</small>
                </strong>
              </div>
            </div>
            <p className="project-report__delta">
              {scoreSummary.delta.toFixed(1)} points of room to grow
            </p>
          </section>

          <button
            type="button"
            className="project-report__resubmit-button"
            onClick={handleStartResubmit}
            disabled={isSubmitting}
          >
            <PencilLine size={17} strokeWidth={2.4} />
            <span>Revise &amp; Resubmit</span>
          </button>
        </aside>

        <main className="project-report__reader">
          <section className="project-report__essay-card">
            <div className="project-report__essay-header">
              <h2>{submission?.name || reportState.project.title}</h2>
              <p>
                {isFileBasedSubmission
                  ? 'File submission'
                  : `${submissionContent.length.toLocaleString()} characters`}
              </p>
            </div>

            {submissionContent ? (
              <pre className="project-report__submission-text">{submissionContent}</pre>
            ) : (
              <p className="project-report__muted">No submission content is available.</p>
            )}
          </section>
        </main>

        <aside className="project-report__feedback-panel">
          <div className="project-report__assistant">
            <button
              type="button"
              className="project-report__assistant-button"
              onClick={() => setIsAssistantOpen((current) => !current)}
              aria-expanded={isAssistantOpen}
              aria-label="Know More About Your Feedback"
            >
              <MessageCircle size={18} strokeWidth={2.4} />
              <span>Know More About Your Feedback</span>
              <ChevronDown
                className={`project-report__assistant-button-chevron ${
                  isAssistantOpen ? 'is-open' : ''
                }`}
                size={16}
                strokeWidth={2.4}
              />
            </button>
          </div>

          {isAssistantOpen ? (
            <div className="project-report__assistant project-report__assistant--floating">
              <section
                className="project-report__assistant-panel"
                aria-label="Know More About Your Feedback"
              >
                <header className="project-report__assistant-header">
                  <div>
                    <span>Know More About Your Feedback</span>
                    <p>Ask about this report and next revision steps.</p>
                  </div>
                  <div className="project-report__assistant-actions">
                    <button
                      type="button"
                      className="project-report__assistant-action"
                      onClick={() => void handleClearChat()}
                      disabled={chatMessages.length === 0 || isChatBusy}
                    >
                      {isChatClearing ? 'Clearing...' : 'Clear'}
                    </button>
                    <button
                      type="button"
                      className="project-report__assistant-close"
                      onClick={() => setIsAssistantOpen(false)}
                      aria-label="Close feedback assistant"
                    >
                      <X size={16} strokeWidth={2.4} />
                    </button>
                  </div>
                </header>

                {chatError ? (
                  <div className="project-report__assistant-error">{chatError}</div>
                ) : null}

                <ChatInterface
                  messages={chatMessages}
                  onSendMessage={handleSendChatMessage}
                  isLoading={isChatBusy}
                  disabled={!isChatAvailable || isChatBusy}
                  loadingLabel={chatLoadingLabel}
                  placeholder={chatPlaceholder}
                  emptyTitle={
                    isChatAvailable ? 'Ask about your feedback' : 'Feedback chat is not ready'
                  }
                  emptyDescription={
                    isChatAvailable
                      ? 'Ask for score explanations, rubric details, or concrete revision ideas.'
                      : 'Wait until AI feedback finishes processing, then return here to chat.'
                  }
                  showToolbar={false}
                  className="project-report__assistant-chat"
                />
              </section>
            </div>
          ) : null}

          {instructorSuggestion ? (
            <section className="project-report__instructor-note">
              <div className="project-report__instructor-note-header">
                <PencilLine size={16} strokeWidth={2.4} />
                <div>
                  <h2>Instructor Suggestion</h2>
                  <p>Shared alongside your AI feedback</p>
                </div>
              </div>
              <p className="project-report__instructor-note-text">
                {instructorSuggestion.suggestion_text}
              </p>
              <span className="project-report__instructor-note-date">
                Updated {formatDate(instructorSuggestion.updated_at)}
              </span>
            </section>
          ) : null}

          {isResubmitOpen ? (
            <section
              ref={resubmitPanelRef}
              className="project-report__card project-report__resubmit-card"
              tabIndex={-1}
            >
              <h2>Submit New Version</h2>
              <p className="project-report__resubmit-guidance" aria-live="polite">
                This form opened here. Add your revised work below, then click Submit Version.
              </p>
              <p className="project-report__muted">
                Previous submissions remain unchanged. ILA will evaluate this as the
                next version.
              </p>

              {isFileBasedSubmission ? (
                <div className="project-report__file-resubmit">
                  <FileDrop
                    onFilesSelected={setResubmitFiles}
                    accept=".pdf"
                    multiple={false}
                    maxSize={50 * 1024 * 1024}
                    className="project-report__filedrop"
                    disabled={isSubmitting}
                  />
                  {resubmitFiles[0] && (
                    <div className="project-report__selected-file">
                      <Upload size={14} strokeWidth={2.4} />
                      <span>{resubmitFiles[0].name}</span>
                    </div>
                  )}
                </div>
              ) : (
                <TextArea
                  value={resubmitText}
                  onChange={setResubmitText}
                  placeholder="Write your revised submission here..."
                  maxLength={50000}
                  showCharCount={true}
                  className="project-report__textarea"
                  rows={8}
                  disabled={isSubmitting}
                />
              )}

              <div className="project-report__resubmit-actions">
                <Button
                  variant="grey"
                  className="project-report__resubmit-action-button"
                  onClick={handleCancelResubmit}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="blue"
                  className="project-report__resubmit-action-button"
                  onClick={handleResubmit}
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Version'}
                </Button>
              </div>
            </section>
          ) : groupedDimensions.length > 0 ? (
            groupedDimensions.map((group) => (
              <section
                key={group.id}
                className="project-report__feedback-group"
                style={
                  {
                    '--group-color': group.color,
                    '--group-tint': hexToRgba(group.color, 0.08),
                    '--group-tint-strong': hexToRgba(group.color, 0.14),
                  } as CSSProperties
                }
              >
                <div className="project-report__feedback-group-header">
                  <span className="project-report__dimension-dot" style={{ backgroundColor: group.color }} />
                  <h2>{group.label}</h2>
                </div>

                {group.dimensions.map((dimension) => {
                  const dimensionIndex = dimensions.findIndex(
                    (candidate) => candidate.dimension_id === dimension.dimension_id,
                  );
                  const color = getDimensionColor(dimension, dimensionIndex);
                  const isSelected = selectedDimension?.dimension_id === dimension.dimension_id;

                  return (
                    <article
                      key={dimension.dimension_id}
                      className={`project-report__feedback-item ${
                        isSelected ? 'is-selected' : ''
                      }`}
                      style={
                        {
                          '--dimension-color': color,
                          '--dimension-tint': hexToRgba(color, 0.08),
                        } as CSSProperties
                      }
                    >
                      <button
                        type="button"
                        className="project-report__feedback-item-toggle"
                        onClick={() =>
                          setSelectedDimensionId((currentDimensionId) =>
                            currentDimensionId === dimension.dimension_id
                              ? null
                              : dimension.dimension_id,
                          )
                        }
                      >
                        <h3>{getShortDimensionLabel(dimension)}</h3>
                        <div className="project-report__feedback-item-meta">
                          <span className="project-report__feedback-level">
                            Level {getScoreValue(dimension).toFixed(1)} <small>/ 3</small>
                          </span>
                          <ChevronDown
                            className={`project-report__feedback-item-chevron ${
                              isSelected ? 'is-open' : ''
                            }`}
                            size={16}
                            strokeWidth={2.4}
                          />
                        </div>
                      </button>

                      {isSelected && (
                        <div className="project-report__feedback-item-body">
                          <div className="project-report__feedback-section">
                            <h4>Feedback</h4>
                            <p>{dimension.ai_reasoning || 'Feedback is not available yet.'}</p>
                          </div>

                          {dimension.ai_strengths?.length ? (
                            <div className="project-report__feedback-section">
                              <h4>Strengths</h4>
                              <ul>
                                {dimension.ai_strengths.map((strength) => (
                                  <li key={strength}>{strength}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}

                          {dimension.ai_improvements?.length ? (
                            <div className="project-report__feedback-section">
                              <h4>Next Steps</h4>
                              <ul>
                                {dimension.ai_improvements.map((improvement) => (
                                  <li key={improvement}>{improvement}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </article>
                  );
                })}
              </section>
            ))
          ) : (
            <section className="project-report__card">
              <h2>Feedback</h2>
              <p className="project-report__muted">
                Feedback will appear here once AI evaluation is complete.
              </p>
            </section>
          )}
        </aside>
      </div>
    </section>
  );
};

export { ProjectReport };
export default ProjectReport;
